import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useLang } from "../../i18n";
import { fileUrl } from "../../lib/api";
import Chapter from "./Chapter";
import Reveal from "./Reveal";

export default function Galeri({ gallery }) {
    const { t } = useLang();
    const [lightbox, setLightbox] = useState(null);
    const touchStartX = useRef(null);

    const close = () => setLightbox(null);
    const prev = (e) => {
        e?.stopPropagation();
        setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
    };
    const next = (e) => {
        e?.stopPropagation();
        setLightbox((i) => (i + 1) % gallery.length);
    };

    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        window.__lenis?.stop();
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
            window.__lenis?.start();
        };
    }, [lightbox, gallery.length]);

    return (
        <section id="galeri" className="py-24 md:py-32 border-t border-racing-line" data-testid="galeri-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <Chapter number="07" title={t.ch_galeri_t} subtitle={t.ch_galeri_s} />

                {gallery.length === 0 ? (
                    <p className="text-racing-muted" data-testid="galeri-empty">{t.galeri_empty}</p>
                ) : (
                    <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
                        {gallery.map((g, i) => (
                            <Reveal key={g.id} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
                                <figure
                                    data-testid={`galeri-item-${i}`}
                                    onClick={() => setLightbox(i)}
                                    className="group relative overflow-hidden border border-racing-line bg-racing-surface cursor-pointer"
                                >
                                    <img
                                        src={fileUrl(g.image_url)}
                                        alt={g.caption || "Galeri kegiatan Mandalika Inlineskate"}
                                        loading="lazy"
                                        className="w-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                                    />
                                    {g.caption ? (
                                        <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            {g.caption}
                                        </figcaption>
                                    ) : null}
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>

            <AnimatePresence>
                {lightbox !== null && gallery[lightbox] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
                        onClick={close}
                        onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
                        onTouchEnd={(e) => {
                            if (touchStartX.current === null) return;
                            const dx = e.changedTouches[0].clientX - touchStartX.current;
                            if (Math.abs(dx) > 50) (dx > 0 ? prev : next)();
                            touchStartX.current = null;
                        }}
                        data-testid="galeri-lightbox"
                    >
                        <button onClick={close} data-testid="lightbox-close" className="absolute top-6 right-6 text-white/70 hover:text-racing-red transition-colors z-10" aria-label="Tutup">
                            <X size={32} weight="bold" />
                        </button>
                        <button onClick={prev} data-testid="lightbox-prev" className="absolute left-4 md:left-8 text-white/70 hover:text-racing-red transition-colors z-10" aria-label="Sebelumnya">
                            <CaretLeft size={44} weight="bold" />
                        </button>
                        <button onClick={next} data-testid="lightbox-next" className="absolute right-4 md:right-8 text-white/70 hover:text-racing-red transition-colors z-10" aria-label="Berikutnya">
                            <CaretRight size={44} weight="bold" />
                        </button>
                        <motion.figure
                            key={gallery[lightbox].id}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="max-w-5xl w-full px-16"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={fileUrl(gallery[lightbox].image_url)}
                                alt={gallery[lightbox].caption || "Foto kegiatan"}
                                className="w-full max-h-[78vh] object-contain"
                                data-testid="lightbox-image"
                            />
                            <figcaption className="mt-4 flex items-center justify-between text-sm text-white/70">
                                <span>{gallery[lightbox].caption}</span>
                                <span className="font-display tracking-widest" data-testid="lightbox-counter">
                                    {lightbox + 1} / {gallery.length}
                                </span>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
